import React from "react";
import { DataGrid, GridToolbarQuickFilter, GridToolbarExport } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";

function isOverflown(element) {
    return (
        element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth
    );
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
    const { width, value } = props;
    const wrapper = React.useRef(null);
    const cellDiv = React.useRef(null);
    const cellValue = React.useRef(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showFullCell, setShowFullCell] = React.useState(false);
    const [showPopper, setShowPopper] = React.useState(false);

    const handleMouseEnter = () => {
        const isCurrentlyOverflown = isOverflown(cellValue.current);
        setShowPopper(isCurrentlyOverflown);
        setAnchorEl(cellDiv.current);
        setShowFullCell(true);
    };

    const handleMouseLeave = () => {
        setShowFullCell(false);
    };

    React.useEffect(() => {
        if (!showFullCell) {
            return undefined;
        }
        function handleKeyDown(nativeEvent) {
            if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
                setShowFullCell(false);
            }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [setShowFullCell, showFullCell]);

    return (
        <Box
            ref={wrapper}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                alignItems: "center",
                lineHeight: "24px",
                width: 1,
                height: 1,
                position: "relative",
                display: "flex",
            }}
        >
            <Box
                ref={cellDiv}
                sx={{
                    height: 1,
                    width,
                    display: "block",
                    position: "absolute",
                    top: 0,
                }}
            />
            <Box
                ref={cellValue}
                sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {value}
            </Box>
            {showPopper && (
                <Popper
                    open={showFullCell && anchorEl !== null}
                    anchorEl={anchorEl}
                    style={{ width, marginLeft: -17 }}
                >
                    <Paper
                        elevation={1}
                        style={{ minHeight: wrapper.current.offsetHeight - 3 }}
                    >
                        <Typography variant="body2" style={{ padding: 8 }}>
                            {value}
                        </Typography>
                    </Paper>
                </Popper>
            )}
        </Box>
    );
});

GridCellExpand.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.instanceOf(React.Element),
        PropTypes.string,
        PropTypes.number,
    ]),
    width: PropTypes.number.isRequired,
};

function renderCellExpand(params) {
    return (
        <GridCellExpand
            value={params.formattedValue || ""}
            width={params.colDef.computedWidth}
        />
    );
}

renderCellExpand.propTypes = {
    colDef: PropTypes.object.isRequired,
    value: PropTypes.string,
};

function displayTextWidth(text, font) {
    let canvas =
        displayTextWidth.canvas ||
        (displayTextWidth.canvas = document.createElement("canvas"));
    let context = canvas.getContext("2d");
    context.font = font;
    let metrics = context.measureText(text);
    return metrics.width;
}

function QuickSearchToolbar() {
    return (
        <Box
            sx={{
                p: 0.5,
                pb: 0,
                display: "flex",
                justifyContent: "space-between",
            }}
        >
            <GridToolbarExport
                printOptions={{ disableToolbarButton: true }}
                csvOptions={{
                    fileName: "OperatorTransactions",
                    delimiter: ",",
                    utf8WithBom: true,
                    fields: [
                        "invoiceDate",
                        "paymentSuccessDate",
                        "name",
                        "email",
                        "externalSubscriberID",
                        "externalSubscriptionID",
                        "stripeCustomerID",
                        "servicePlanID",
                        "servicePlanName",
                        "servicePlanDescription",
                        "externalServicePlanID",
                        "venuePriceAdjustment",
                        "venueUnitPriceAdjustment",
                        "billingCyclePrice",
                        "listBillingCyclePrice",
                        "netBillingCyclePrice",
                        "amountReceived",
                        "unitID_internal",
                        "externalVenueUnitID",
                        "externalVenueID",
                        "venueID",
                        "venueName",
                    ],
                }}
            />
            <GridToolbarQuickFilter />
        </Box>
    );
}

export default function TransactionsTableMUI(props) {
    let userAttributes;
    let columns;
    let rows;
    let i = 0;

    const firstCurrencyValue = JSON.parse(
        sessionStorage.getItem("currencyNames")
    )[0];

    if (props.transactions !== undefined) {
        userAttributes = [];
        for (const key of Object.keys(props.transactions)) {
            const txn = props.transactions[key];
            const currencySymbol = txn["CurrencySymbol"] || "";
            const currency = txn["Currency"] || firstCurrencyValue;

            userAttributes.push({
                id: i,
                // Displayed columns
                invoiceDate:
                    txn["InvoiceDateTimeUTC.ISO8601"] !== ""
                        ? Intl.DateTimeFormat("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "numeric",
                              minute: "numeric",
                          }).format(
                              new Date(txn["InvoiceDateTimeUTC.ISO8601"])
                          )
                        : "",
                paymentSuccessDate:
                    txn["PaymentSuccessDateTimeUTC.ISO8601"] &&
                    txn["PaymentSuccessDateTimeUTC.ISO8601"] !== ""
                        ? Intl.DateTimeFormat("en-CA", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "numeric",
                              minute: "numeric",
                          }).format(
                              new Date(
                                  txn["PaymentSuccessDateTimeUTC.ISO8601"]
                              )
                          )
                        : "",
                name:
                    (txn["GivenName"] || "") +
                    " " +
                    (txn["FamilyName"] || ""),
                email: txn["EmailAddress"] || "",
                servicePlan: {
                    planName: txn["ServicePlanName"] || "",
                    planDescription: txn["ServicePlanDescription"] || "",
                },
                servicePlanName: txn["ServicePlanName"] || "",
                servicePlanDescription: txn["ServicePlanDescription"] || "",
                billingCyclePrice:
                    txn["BillingCyclePrice." + currency] !== undefined
                        ? currencySymbol +
                          (txn["BillingCyclePrice." + currency] / 100).toFixed(2)
                        : "",
                amountReceived:
                    txn["AmountReceived." + currency] !== undefined &&
                    txn["AmountReceived." + currency] !== "undefined"
                        ? currencySymbol +
                          (txn["AmountReceived." + currency] / 100).toFixed(2)
                        : "",
                venueName: txn["VenueName"] || "",
                venueAddress:
                    (txn["AddressLine1"] || "") +
                    (txn["AddressLine2"] && txn["AddressLine2"] !== ""
                        ? " " + txn["AddressLine2"]
                        : "") +
                    ", " +
                    (txn["City"] || "") +
                    ", " +
                    (txn["State"] || "") +
                    " " +
                    (txn["PostalCode"] || ""),
                // CSV-only columns (hidden in grid)
                externalSubscriberID: txn["externalSubscriberID"] || "",
                externalSubscriptionID: txn["externalSubscriptionID"] || "",
                stripeCustomerID: txn["StripeCustomerID"] || "",
                servicePlanID: txn["ServicePlanID"] || "",
                externalServicePlanID: txn["externalServicePlanID"] || "",
                venuePriceAdjustment:
                    txn["VenuePriceAdjustment"] !== undefined
                        ? currencySymbol +
                          (txn["VenuePriceAdjustment"] / 100).toFixed(2)
                        : "",
                venueUnitPriceAdjustment:
                    txn["VenueUnitPriceAdjustment"] !== undefined
                        ? currencySymbol +
                          (txn["VenueUnitPriceAdjustment"] / 100).toFixed(2)
                        : "",
                listBillingCyclePrice:
                    txn["ListBillingCyclePrice." + currency] !== undefined
                        ? currencySymbol +
                          (txn["ListBillingCyclePrice." + currency] / 100).toFixed(2)
                        : "",
                netBillingCyclePrice:
                    txn["NetBillingCyclePrice." + currency] !== undefined
                        ? currencySymbol +
                          (txn["NetBillingCyclePrice." + currency] / 100).toFixed(2)
                        : "",
                unitID_internal: txn["UnitID_internal"] || "",
                externalVenueUnitID: txn["externalVenueUnitID"] || "",
                externalVenueID: txn["externalVenueID"] || "",
                venueID: txn["VenueID"] || "",
            });
            i = i + 1;
        }
    }

    columns = [
        {
            headerName: "Invoice Date",
            field: "invoiceDate",
            minWidth: 150,
            renderCell: renderCellExpand,
        },
        {
            headerName: "Payment Success Date",
            field: "paymentSuccessDate",
            minWidth: 150,
            renderCell: renderCellExpand,
        },
        {
            headerName: "Name",
            field: "name",
            minWidth: 150,
            renderCell: renderCellExpand,
        },
        {
            headerName: "Email",
            field: "email",
            minWidth: 200,
            renderCell: renderCellExpand,
        },
        {
            headerName: "Service Plan",
            field: "servicePlan",
            minWidth: 200,
            renderCell: (params) => (
                <GridCellExpand
                    value={
                        (
                            <div>
                                {params.value.planName}
                                <br />
                                {params.value.planDescription}
                            </div>
                        ) || ""
                    }
                    width={displayTextWidth(
                        params.value,
                        ".9rem Proxima Nova Rg"
                    )}
                />
            ),
        },
        {
            headerName: "Billing Cycle Price",
            field: "billingCyclePrice",
            minWidth: 140,
            renderCell: renderCellExpand,
        },
        {
            headerName: "Amount Received",
            field: "amountReceived",
            minWidth: 140,
            renderCell: renderCellExpand,
        },
        {
            headerName: "Venue Name",
            field: "venueName",
            minWidth: 150,
            renderCell: renderCellExpand,
        },
        {
            headerName: "Address",
            field: "venueAddress",
            minWidth: 200,
            renderCell: renderCellExpand,
        },
        // Hidden columns included in CSV export
        {
            headerName: "Service Plan Name",
            field: "servicePlanName",
            minWidth: 120,
        },
        {
            headerName: "Service Plan Description",
            field: "servicePlanDescription",
            minWidth: 120,
        },
        {
            headerName: "External Subscriber ID",
            field: "externalSubscriberID",
            minWidth: 120,
        },
        {
            headerName: "External Subscription ID",
            field: "externalSubscriptionID",
            minWidth: 120,
        },
        {
            headerName: "Stripe Customer ID",
            field: "stripeCustomerID",
            minWidth: 120,
        },
        {
            headerName: "Service Plan ID",
            field: "servicePlanID",
            minWidth: 120,
        },
        {
            headerName: "External Service Plan ID",
            field: "externalServicePlanID",
            minWidth: 120,
        },
        {
            headerName: "Venue Price Adjustment",
            field: "venuePriceAdjustment",
            minWidth: 120,
        },
        {
            headerName: "Venue Unit Price Adjustment",
            field: "venueUnitPriceAdjustment",
            minWidth: 120,
        },
        {
            headerName: "List Billing Cycle Price",
            field: "listBillingCyclePrice",
            minWidth: 120,
        },
        {
            headerName: "Net Billing Cycle Price",
            field: "netBillingCyclePrice",
            minWidth: 120,
        },
        {
            headerName: "Unit ID (Internal)",
            field: "unitID_internal",
            minWidth: 120,
        },
        {
            headerName: "External Venue Unit ID",
            field: "externalVenueUnitID",
            minWidth: 120,
        },
        {
            headerName: "External Venue ID",
            field: "externalVenueID",
            minWidth: 120,
        },
        {
            headerName: "Venue ID",
            field: "venueID",
            minWidth: 120,
        },
    ];

    rows = userAttributes;

    return (
        <div style={{ height: 400, width: "100%" }}>
            <DataGrid
                rows={rows}
                columns={columns}
                getRowHeight={({ id, densityFactor }) => {
                    return 70 * densityFactor;
                }}
                sx={{
                    overflowX: "scroll",
                    "& .MuiDataGrid-row:hover": {
                        backgroundColor: "inherit",
                        cursor: "default",
                    },
                }}
                slots={{ toolbar: QuickSearchToolbar }}
                hideFooterPagination={false}
                hideFooter={false}
                rowSelection={false}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            servicePlanName: false,
                            servicePlanDescription: false,
                            externalSubscriberID: false,
                            externalSubscriptionID: false,
                            stripeCustomerID: false,
                            servicePlanID: false,
                            externalServicePlanID: false,
                            venuePriceAdjustment: false,
                            venueUnitPriceAdjustment: false,
                            listBillingCyclePrice: false,
                            netBillingCyclePrice: false,
                            unitID_internal: false,
                            externalVenueUnitID: false,
                            externalVenueID: false,
                            venueID: false,
                        },
                    },
                }}
            />
        </div>
    );
}
